import { supabase } from '../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

export type SubscriptionTier = 'free' | 'pro';

export interface SubscriptionStatus {
  isActive: boolean;
  isPro: boolean;
  tier: SubscriptionTier;
  expiresAt: string | null;
  isTrial?: boolean;
}

export const subscriptionService = {
  async getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
    if (!userId) {
      return {
        isActive: false,
        isPro: false,
        tier: 'free',
        expiresAt: null,
        isTrial: false
      };
    }

    try {
      // Get the user's subscription from the subscriptions table
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, tier, expires_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }

      if (!data) {
        return {
          isActive: false,
          isPro: false,
          tier: 'free',
          expiresAt: null,
          isTrial: false
        };
      }

      const now = new Date();
      const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
      const isActive = data.status === 'active' || (data.status === 'trial' && (!expiresAt || expiresAt > now));

      return {
        isActive,
        isPro: data.tier === 'pro' && isActive,
        tier: data.tier as SubscriptionTier,
        expiresAt: data.expires_at,
        isTrial: data.status === 'trial'
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      // Instead of throwing, return free tier on error
      return {
        isActive: false,
        isPro: false,
        tier: 'free',
        expiresAt: null,
        isTrial: false
      };
    }
  },

  async createCheckoutSession(userId: string, billingPeriod: 'monthly' | 'yearly'): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No session found');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout?billingPeriod=${billingPeriod}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create checkout session: ${errorText}`);
    }

    const { url } = await response.json();
    if (!url) {
      throw new Error('No checkout URL returned from server');
    }

    window.location.href = url;
  },

  async createPortalSession(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-webhook', {
        body: { type: 'create-portal-session', userId }
      });

      if (error) throw error;

      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  },

  async activateFreeTrial(userId: string): Promise<SubscriptionStatus> {
    if (!userId) {
      throw new Error('No userId provided');
    }

    // Check if user has had a trial before by looking at all their subscriptions
    const { data: existingSubscriptions, error: trialCheckError } = await supabase
      .from('subscriptions')
      .select('has_used_trial, trial_end_at, status')
      .eq('user_id', userId);

    if (trialCheckError) {
      throw trialCheckError;
    }

    // Check if any subscription indicates they've used their trial
    const hasUsedTrial = existingSubscriptions?.some(sub => 
      sub.has_used_trial || 
      (sub.trial_end_at && new Date(sub.trial_end_at) > new Date()) ||
      sub.status === 'trial'
    );

    if (hasUsedTrial) {
      throw new Error('User has already used their free trial');
    }

    // Calculate trial dates
    const trialStartAt = new Date();
    const trialEndAt = new Date();
    trialEndAt.setDate(trialEndAt.getDate() + 7);

    // Deactivate any existing subscriptions
    if (existingSubscriptions?.length > 0) {
      const { error: updateError } = await supabase
        .from('subscriptions')
        .update({ status: 'inactive' })
        .eq('user_id', userId);

      if (updateError) {
        throw updateError;
      }
    }

    // Create new trial subscription
    const { data: subscription, error: subscriptionError } = await supabase
      .from('subscriptions')
      .insert({
        user_id: userId,
        status: 'trial',
        tier: 'pro',
        trial_start_at: trialStartAt.toISOString(),
        trial_end_at: trialEndAt.toISOString(),
        has_used_trial: true,
        expires_at: trialEndAt.toISOString()
      })
      .select()
      .single();

    if (subscriptionError) {
      throw subscriptionError;
    }

    return {
      isActive: true,
      isPro: true,
      tier: 'pro',
      expiresAt: subscription.expires_at,
      isTrial: true
    };
  },
};
